import type { ComponentRegistryEntry, SDUIComponentProps } from '@/types/sduiTypes'

// SDUI adapter components that wrap existing UI primitives
import SDUILabel from '../components/SDUILabel'
import SDUITextInput from '../components/SDUITextInput'
import SDUIButton from '../components/SDUIButton'
import SDUISelect from '../components/SDUISelect'
import SDUIRadio from '../components/SDUIRadio'
import SDUISwitch from '../components/SDUISwitch'
import SDUIGrid from '../components/SDUIGrid'
import SDUIActionButton from '../components/SDUIActionButton'

/**
 * Component Registry for SDUI
 *
 * Maps backend component type strings to actual React components.
 * Each component is wrapped in an SDUI adapter that normalizes
 * the prop interface between the backend schema and existing UI primitives.
 */
export const COMPONENT_REGISTRY: Record<string, ComponentRegistryEntry> = {
    label: {
        component: SDUILabel,
        isFormField: false,
        apiTypeAlias: 'Label',
        propertyConfig: { tokenRole: 'text' },
    },
    textInput: {
        component: SDUITextInput,
        isFormField: true,
        defaultProps: { autoComplete: 'off' },
        apiTypeAlias: 'Input',
        defaultModelValue: '',
        propertyConfig: { tokenRole: 'placeholder' },
    },
    button: {
        component: SDUIButton,
        isFormField: false,
        apiTypeAlias: 'Button',
        propertyConfig: { tokenRole: 'label' },
    },
    select: {
        component: SDUISelect,
        isFormField: true,
        apiTypeAlias: 'Select',
        defaultModelValue: null,
        propertyConfig: { tokenRole: 'label', hasOptions: true },
    },
    radio: {
        component: SDUIRadio,
        isFormField: true,
        apiTypeAlias: 'Radio',
        defaultModelValue: null,
        propertyConfig: { tokenRole: 'label', hasOptions: true },
    },
    switch: {
        component: SDUISwitch,
        isFormField: true,
        apiTypeAlias: 'Switch',
        defaultModelValue: false,
        propertyConfig: { tokenRole: 'label' },
    },
    grid: {
        component: SDUIGrid,
        isFormField: false,
        isLayout: true,
        apiTypeAlias: 'Grid',
        propertyConfig: { tokenRole: 'endpoint', descriptionRole: 'title' },
    },
    actionButton: {
        component: SDUIActionButton,
        isFormField: true,
        apiTypeAlias: 'ActionButton',
        defaultModelValue: '',
        propertyConfig: { tokenRole: 'placeholder' },
    },
}

/**
 * Resolve a component type string to its React component.
 * Returns null and logs a warning if the type is unknown.
 */
export function resolveComponent(
    type: string,
): React.ComponentType<SDUIComponentProps> | null {
    const entry = COMPONENT_REGISTRY[type]
    if (!entry) {
        console.warn(`[SDUI] Unknown component type: "${type}"`)
        return null
    }
    return entry.component
}

/**
 * Check if a given component type is a form field (i.e., has data binding).
 */
export function isFormFieldType(type: string): boolean {
    return COMPONENT_REGISTRY[type]?.isFormField ?? false
}

/**
 * Check if a given component type is a layout/container (e.g. grid).
 * Layout types receive height: 100% on their wrapper so they fill the allocated cell.
 */
export function isLayoutType(type: string): boolean {
    return COMPONENT_REGISTRY[type]?.isLayout ?? false
}

/**
 * Get default props for a given component type.
 */
export function getDefaultProps(
    type: string,
): Record<string, unknown> | undefined {
    return COMPONENT_REGISTRY[type]?.defaultProps
}

/**
 * Resolve an SDUI type string from a backend API type alias.
 * Returns null if no registry entry declares that alias.
 */
export function resolveTypeFromAlias(apiType: string): string | null {
    const entry = Object.entries(COMPONENT_REGISTRY).find(
        ([, v]) => v.apiTypeAlias === apiType,
    )
    return entry ? entry[0] : null
}

/**
 * Return the default data-model value for a form field type.
 * Falls back to '' for unknown types.
 */
export function getDefaultModelValue(type: string): unknown {
    return COMPONENT_REGISTRY[type]?.defaultModelValue ?? ''
}

/**
 * Build the `properties` object for a ComponentSchema using only the
 * registry's propertyConfig — no per-type if/else needed in the service.
 */
export function buildSchemaProperties(
    type: string,
    token: string,
    description: string,
    enabled: boolean,
): Record<string, unknown> {
    const config = COMPONENT_REGISTRY[type]?.propertyConfig
    if (!config) return {}

    const props: Record<string, unknown> = {}

    if (config.tokenRole === 'text') {
        props.text = token || description
    } else if (config.tokenRole === 'label') {
        props.label = token || description
    } else if (config.tokenRole === 'endpoint') {
        props.endpoint = token
    } else {
        props.placeholder = token || description
    }

    if (config.descriptionRole) props[config.descriptionRole] = description
    if (type !== 'label') props.disabled = !enabled
    if (config.hasOptions) props.options = []

    return props
}
